import { useCallback, useEffect, useRef, useState } from 'react';
import { ButtonBase, Icon, Box, styled } from '@mui/material';
import { useLocation } from 'react-router-dom';
import clsx from 'clsx';

/* ─── Design Tokens (mirror MatxVerticalNav) ─────────────────── */
const tokens = {
	accent: '#6EE7B7',
	accentGlow: 'rgba(110,231,183,0.18)',
	accentBg: 'rgba(110,231,183,0.10)',
	surfaceHover: 'rgba(255,255,255,0.07)',
	textPrimary: 'rgba(255,255,255,0.92)',
	textMuted: 'rgba(255,255,255,0.55)',
	border: 'rgba(255,255,255,0.06)',
	radius: '10px',
	transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
};

/* ─── Root wrapper ───────────────────────────────────────────── */
const NavExpandRoot = styled('div')(() => ({
	fontFamily: "'Plus Jakarta Sans', sans-serif",

	/* Chevron states */
	'& .chevron-icon': {
		transition: 'transform 0.25s cubic-bezier(0.4,0,0.2,1)',
		transform: 'rotate(0deg)',
		color: 'rgba(255,255,255,0.28)',
		fontSize: '16px !important',
		display: 'flex',
		alignItems: 'center',
	},
	'& .chevron-icon.open': {
		transform: 'rotate(90deg)',
		color: tokens.accent,
	},

	/* Expansion panel slide */
	'& .expansion-panel': {
		overflow: 'hidden',
		transition: 'max-height 0.28s cubic-bezier(0.4,0,0.2,1)',
		/* indent children */
		paddingLeft: '10px',
		borderLeft: `1px solid ${tokens.border}`,
		marginLeft: '19px',
		marginBottom: '2px',
	},

	/* Compact mode */
	'&.compactNavItem': {
		width: 44,
		overflow: 'hidden',
		justifyContent: 'center !important',
		'& .itemText': { display: 'none' },
		'& .itemIcon': { display: 'none' },
		'& .expansion-panel': { display: 'none' },
	},
}));

/* ─── Trigger button ─────────────────────────────────────────── */
const BaseButton = styled(ButtonBase)(() => ({
	position: 'relative',
	height: '42px',
	width: '100%',
	whiteSpace: 'pre',
	overflow: 'hidden',
	paddingRight: '10px',
	borderRadius: tokens.radius,
	marginBottom: '2px',
	display: 'flex',
	justifyContent: 'space-between !important',
	alignItems: 'center',
	color: tokens.textMuted,
	transition: tokens.transition,

	'&:hover': {
		background: tokens.surfaceHover,
		color: tokens.textPrimary,
		'& .group-icon': { color: tokens.accent },
		'& .group-label': { color: tokens.textPrimary },
	},

	/* Active-open state */
	'&.open': {
		color: tokens.textPrimary,
		'& .group-icon': { color: tokens.accent },
	},

	'& .group-icon': {
		width: '38px',
		fontSize: '17px',
		paddingLeft: '14px',
		paddingRight: '6px',
		verticalAlign: 'middle',
		color: 'rgba(255,255,255,0.35)',
		transition: tokens.transition,
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
	},
}));

/* ─── Item label ─────────────────────────────────────────────── */
const ItemText = styled('span')(() => ({
	fontFamily: "'Plus Jakarta Sans', sans-serif",
	fontSize: '13.5px',
	fontWeight: 500,
	paddingLeft: '0.4rem',
	flex: 1,
	color: 'inherit',
	transition: tokens.transition,
	textAlign: 'left',
}));

/* ─── Bullet for icon-less items ─────────────────────────────── */
const BulletIcon = styled('div')(() => ({
	width: '5px',
	height: '5px',
	borderRadius: '50%',
	background: 'rgba(255,255,255,0.22)',
	marginLeft: '26px',
	marginRight: '12px',
	flexShrink: 0,
	transition: tokens.transition,
}));

/* ─── Badge ──────────────────────────────────────────────────── */
const BadgeValue = styled('div')(() => ({
	fontFamily: "'Plus Jakarta Sans', sans-serif",
	fontSize: '10px',
	fontWeight: 700,
	padding: '2px 7px',
	borderRadius: '20px',
	background: tokens.accentGlow,
	color: tokens.accent,
	border: `1px solid ${tokens.accentGlow}`,
	marginRight: '6px',
	letterSpacing: '0.02em',
	flexShrink: 0,
}));

/* ─── Component ──────────────────────────────────────────────── */
const MatxVerticalNavExpansionPanel = ({ item, children, mode }) => {
	const [collapsed, setCollapsed] = useState(true);
	const elementRef = useRef(null);
	const componentHeight = useRef(0);
	const { pathname } = useLocation();
	const { name, icon, iconText, badge } = item;

	const handleClick = () => {
		componentHeight.current = 0;
		calculateHeight(elementRef.current);
		setCollapsed((prev) => !prev);
	};

	const calculateHeight = useCallback((node) => {
		if (node.name !== 'child') {
			for (let child of node.children) calculateHeight(child);
		}
		if (node.name === 'child') componentHeight.current += node.scrollHeight;
		else componentHeight.current += 44;
	}, []);

	useEffect(() => {
		if (!elementRef.current) return;
		calculateHeight(elementRef.current);
		for (let child of elementRef.current.children) {
			if (child.getAttribute('href') === pathname) {
				setCollapsed(false);
			}
		}
	}, [pathname, calculateHeight]);

	return (
		<NavExpandRoot className={clsx({ compactNavItem: mode === 'compact' })}>
			<BaseButton
				className={clsx('has-submenu', {
					compactNavItem: mode === 'compact',
					open: !collapsed,
				})}
				onClick={handleClick}>
				<Box display='flex' alignItems='center' flex={1} overflow='hidden'>
					{icon && <Icon className='group-icon'>{icon}</Icon>}
					{!icon && iconText && <BulletIcon />}
					<ItemText className='group-label sidenavHoverShow'>{name}</ItemText>
				</Box>

				{badge && (
					<BadgeValue className='sidenavHoverShow itemIcon'>
						{badge.value}
					</BadgeValue>
				)}

				<Icon
					className={clsx('chevron-icon sidenavHoverShow', {
						open: !collapsed,
					})}
					sx={{ fontSize: '16px', mr: '2px' }}>
					chevron_right
				</Icon>
			</BaseButton>

			{/* Children panel */}
			<div
				ref={elementRef}
				className='expansion-panel submenu'
				style={
					collapsed
						? { maxHeight: '0px' }
						: { maxHeight: `${componentHeight.current}px` }
				}>
				{children}
			</div>
		</NavExpandRoot>
	);
};

export default MatxVerticalNavExpansionPanel;
